import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { submitTimeService } from '@services/submitTimeService';
import { apiService } from '@services/api';

const REMINDER_OPEN_KEY = 'ReminderDialogOpen';
const DAILY_HOURS_KEY = 'DailyHoursLogged';
const DAILY_HOURS_DATE_KEY = 'DailyHoursLoggedDate';

function getTodayDateKey(): string
{
  return new Date().toISOString().slice(0, 10);
}

function isAfterCutoff(): boolean
{
  const cutoff = new Date();
  cutoff.setHours(16, 30, 0, 0);
  return new Date() >= cutoff;
}

function isDailyHoursLogged(): boolean
{
  const storedDate = localStorage.getItem(DAILY_HOURS_DATE_KEY);
  if (storedDate !== getTodayDateKey())
  {
    localStorage.removeItem(DAILY_HOURS_KEY);
    localStorage.removeItem(DAILY_HOURS_DATE_KEY);
    return false;
  }
  return localStorage.getItem(DAILY_HOURS_KEY) === 'true';
}

async function fetchDailyHoursTotal(): Promise<number>
{
  const today = getTodayDateKey();
  const entries = await submitTimeService.getEvents({
    StartDate: `${today}T00:00:00`,
    EndDate: `${today}T00:00:00`,
  });
  let totalMinutes = 0;
  for (const entry of entries)
  {
    const parts = entry.Hours.split(':');
    totalMinutes += (Number(parts[0]) || 0) * 60 + (Number(parts[1]) || 0);
  }
  return totalMinutes / 60;
}

function setDailyHoursLogged(logged: boolean): void
{
  localStorage.setItem(DAILY_HOURS_KEY, String(logged));
  localStorage.setItem(DAILY_HOURS_DATE_KEY, getTodayDateKey());
}

export function useLogHoursReminder()
{
  const { GivenName } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const checkingRef = useRef(false);

  const validateAndShow = useCallback(async () =>
  {
    if (checkingRef.current) return;
    if (localStorage.getItem(REMINDER_OPEN_KEY) === 'true') return;
    if (!isAfterCutoff()) return;
    if (isDailyHoursLogged()) return;

    checkingRef.current = true;
    try
    {
      const totalHours = await fetchDailyHoursTotal();
      const logged = totalHours >= 8;
      setDailyHoursLogged(logged);
      if (logged) return;
    }
    catch
    {
      // If API fails, rely on the cached localStorage value
    }
    finally
    {
      checkingRef.current = false;
    }

    localStorage.setItem(REMINDER_OPEN_KEY, 'true');

    if (intervalRef.current)
    {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsOpen(true);

    setTimeout(() =>
    {
      localStorage.removeItem(REMINDER_OPEN_KEY);
    }, 500);
  }, []);

  const handleClose = useCallback(() =>
  {
    setIsOpen(false);
    localStorage.removeItem(REMINDER_OPEN_KEY);
  }, []);

  const handleDontRemindAgain = useCallback(() =>
  {
    setIsOpen(false);
    localStorage.removeItem(REMINDER_OPEN_KEY);
    apiService.post('Person/RemoveLogHoursReminder', {}).catch(() => {});
  }, []);

  // Refresh daily hours when user logs time from SubmitTime
  const refreshDailyHours = useCallback(async () =>
  {
    try
    {
      const totalHours = await fetchDailyHoursTotal();
      setDailyHoursLogged(totalHours >= 8);
    }
    catch { /* ignore */ }
  }, []);

  useEffect(() =>
  {
    const initialTimeout = setTimeout(() => validateAndShow(), 2000);

    intervalRef.current = setInterval(() => validateAndShow(), 60000);

    const handleTimeLogged = () => { refreshDailyHours(); };
    window.addEventListener('C2_timeLogged', handleTimeLogged);

    return () =>
    {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('C2_timeLogged', handleTimeLogged);
    };
  }, [validateAndShow, refreshDailyHours]);

  // Re-check on route changes
  useEffect(() =>
  {
    const handlePopState = () =>
    {
      setTimeout(() => validateAndShow(), 2000);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [validateAndShow]);

  const fullName = sessionStorage.getItem('C2_personName') || GivenName();

  return {
    isOpen,
    personName: fullName,
    onClose: handleClose,
    onDontRemindAgain: handleDontRemindAgain,
  };
}
